import { FormEvent, useEffect, useMemo, useState } from "react";
import { sortQuoteLines } from "./quote-line-order";
import {
  CaseQuoteDocument,
  CaseQuoteDocumentResponse,
  CaseQuoteHistoryCandidate,
  CaseQuoteLine,
  CaseQuoteSourcingCandidates,
  CaseQuoteSourcingCandidate,
  loadCaseQuoteDocument,
  loadQuoteSourcingCandidates,
  saveCaseQuoteDocument,
} from "./api";

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const currencies = [
  "USD",
  "CNY",
  "EUR",
  "JPY",
  "GBP",
  "AUD",
  "CAD",
  "CHF",
  "SGD",
];
const units = ["ug", "mg", "g", "kg", "t", "uL", "mL", "L", "item", "other"];
const statusName: Record<string, string> = {
  DRAFT: "待正式保存",
  SUBMITTED: "正式版本",
  SUPERSEDED: "历史版本",
};
const number = (value: string | null | undefined) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};
const money = (value: number | string | null, currency: string) =>
  value === null || value === ""
    ? "—"
    : `${currency} ${Number(value).toLocaleString("zh-CN", { maximumFractionDigits: 4 })}`;
const blankLine = (currency: string): CaseQuoteLine => ({
  sourceSourcingProductId: null,
  sourcePriceTierId: null,
  productName: "",
  catalogNumber: "",
  casNo: "",
  requestedQuantity: "",
  purity: "",
  specification: "",
  quantity: "1",
  specValue: "",
  specUnit: "g",
  unitPrice: "",
  currency,
  leadTime: "",
  supplierName: null,
  costUnitPrice: null,
  costCurrency: null,
  costValidUntil: null,
  lineSubtotal: null,
  lineCost: null,
  grossProfit: null,
  grossMarginBps: null,
});

export function buildQuotePayload(
  source: CaseQuoteDocumentResponse,
  reuseQuoteDocumentId?: string | null,
) {
  const document = source.document;
  return {
    ...(document.id ? { documentId: document.id } : {}),
    ...(document.id ? { expectedLockVersion: document.lockVersion } : {}),
    ...(reuseQuoteDocumentId ? { reuseQuoteDocumentId } : {}),
    quoteNumber: document.quoteNumber,
    prefix: document.prefix,
    customerName: document.customerName,
    organization: document.organization,
    contact: document.contact,
    paymentTerms: document.paymentTerms,
    country: document.country,
    shippingAddress: document.shippingAddress,
    billingAddress: document.billingAddress,
    quoteDate: document.quoteDate,
    validUntil: document.validUntil,
    currency: document.currency,
    shippingFee: document.shippingFee || "0",
    handlingFee: document.handlingFee || "0",
    omitTotal: document.omitTotal ?? false,
    internalNotes: document.internalNotes,
    reason: document.id ? "保存报价单修改" : "从案件查货结果创建报价单",
    lines: sortQuoteLines(document.lines).map((line) => ({
      sourceSourcingProductId: line.sourceSourcingProductId,
      sourcePriceTierId: line.sourcePriceTierId,
      productName: line.productName,
      catalogNumber: line.catalogNumber,
      casNo: line.casNo,
      requestedQuantity: line.requestedQuantity,
      purity: line.purity ?? "",
      specification: line.specification,
      quantity: line.quantity || "1",
      specValue: line.specValue,
      specUnit: line.specUnit,
      unitPrice: line.unitPrice,
      currency: line.currency,
      leadTime: line.leadTime,
    })),
  };
}

export function appendSourcingQuoteLines(
  existing: CaseQuoteLine[],
  incoming: CaseQuoteLine[],
  currency: string,
) {
  const keys = new Set(
    existing.map((line) => line.sourcePriceTierId).filter(Boolean),
  );
  const lines = [...existing];
  for (const line of incoming) {
    if (
      !line.sourcePriceTierId ||
      !line.sourceSourcingProductId ||
      keys.has(line.sourcePriceTierId)
    )
      continue;
    keys.add(line.sourcePriceTierId);
    lines.push({ ...clone(line), currency, unitPrice: "" });
  }
  return lines;
}

function groupSourcingCandidates(candidates: CaseQuoteSourcingCandidate[]) {
  const groups = new Map<string, CaseQuoteSourcingCandidate[]>();
  for (const candidate of candidates) {
    const productId = candidate.line.sourceSourcingProductId!;
    const group = groups.get(productId) ?? [];
    group.push(candidate);
    groups.set(productId, group);
  }
  return [...groups.entries()];
}

export function CaseQuotePanel({
  caseId,
  disabled,
  canManage,
  onChanged,
}: {
  caseId: string;
  disabled: boolean;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [data, setData] = useState<CaseQuoteDocumentResponse | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [sourcing, setSourcing] = useState<CaseQuoteSourcingCandidates | null>(
    null,
  );
  const [selectedTiers, setSelectedTiers] = useState<string[]>([]);
  const [loadingSourcing, setLoadingSourcing] = useState(false);
  const [sourcingError, setSourcingError] = useState("");
  const [notice, setNotice] = useState("");
  const [reuseQuoteDocumentId, setReuseQuoteDocumentId] = useState<
    string | null
  >(null);

  async function chooseSourcing() {
    setSourcing(null);
    setLoadingSourcing(true);
    setSourcingError("");
    setNotice("");
    try {
      setSourcing(await loadQuoteSourcingCandidates(caseId));
      setSelectedTiers([]);
    } catch (reason) {
      setSourcingError(
        reason instanceof Error ? reason.message : String(reason),
      );
    } finally {
      setLoadingSourcing(false);
    }
  }

  function appendSelectedSourcing() {
    if (!data || !sourcing) return;
    const incoming = sourcing.candidates
      .filter(
        (candidate) =>
          !candidate.expired &&
          selectedTiers.includes(candidate.line.sourcePriceTierId ?? ""),
      )
      .map((candidate) => candidate.line);
    const lines = appendSourcingQuoteLines(
      data.document.lines,
      incoming,
      data.document.currency,
    );
    if (lines.length > 100) {
      setSourcingError("报价产品最多100行，请减少选择。");
      return;
    }
    updateDocument({ lines });
    setNotice(
      `已追加 ${lines.length - data.document.lines.length} 个报价项，请核对包装数、客户单价及报价有效期后保存。`,
    );
    setSourcing(null);
  }

  async function reload(signal?: AbortSignal) {
    setReuseQuoteDocumentId(null);
    setSourcing(null);
    setNotice("");
    setLoading(true);
    setError("");
    try {
      const response = await loadCaseQuoteDocument(caseId, signal);
      setData(response);
      setDirty(false);
      return response;
    } catch (reason) {
      if (!signal?.aborted)
        setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
    return null;
  }

  useEffect(() => {
    const controller = new AbortController();
    void reload(controller.signal);
    return () => controller.abort();
  }, [caseId]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (!editing || !open) {
      setSourcing(null);
      setSelectedTiers([]);
    }
  }, [editing, open]);

  const preview = useMemo(() => {
    if (!data) return null;
    const doc = data.document;
    const subtotal = doc.lines.reduce(
      (sum, line) => sum + number(line.quantity) * number(line.unitPrice),
      0,
    );
    const total = doc.omitTotal
      ? null
      : subtotal + number(doc.shippingFee) + number(doc.handlingFee);
    const comparable =
      data.costVisible &&
      doc.lines.length > 0 &&
      doc.lines.every(
        (line) =>
          line.costUnitPrice !== null && line.costCurrency === doc.currency,
      );
    const totalCost = comparable
      ? doc.lines.reduce(
          (sum, line) =>
            sum + number(line.quantity) * number(line.costUnitPrice),
          0,
        )
      : null;
    const grossProfit =
      totalCost === null || total === null ? null : total - totalCost;
    return {
      subtotal,
      total,
      totalCost,
      grossProfit,
      grossMargin:
        grossProfit === null || total === null || total === 0
          ? null
          : (grossProfit / total) * 100,
    };
  }, [data]);
  const latestVersion = data?.versions[0] ?? data?.document;
  const viewingLatest = Boolean(
    data &&
    (!data.document.id ||
      !latestVersion?.id ||
      data.document.id === latestVersion.id),
  );

  function updateDocument(patch: Partial<CaseQuoteDocument>) {
    setData((current) => {
      if (!current) return current;
      const next = clone(current);
      next.document = { ...next.document, ...patch };
      return next;
    });
    setDirty(true);
  }

  function updateLine(index: number, patch: Partial<CaseQuoteLine>) {
    setData((current) => {
      if (!current) return current;
      const next = clone(current);
      next.document.lines[index] = {
        ...next.document.lines[index]!,
        ...patch,
      };
      return next;
    });
    setDirty(true);
  }

  function setCurrency(currency: string) {
    setData((current) => {
      if (!current) return current;
      const next = clone(current);
      next.document.currency = currency;
      next.document.lines = next.document.lines.map((line) => ({
        ...line,
        currency,
      }));
      return next;
    });
    setDirty(true);
  }

  function applyHistory(candidate: CaseQuoteHistoryCandidate) {
    if (!data) return;
    const current = data.document;
    const historical = candidate.document;
    setReuseQuoteDocumentId(candidate.documentId);
    updateDocument({
      paymentTerms: historical.paymentTerms,
      country: historical.country,
      shippingAddress: historical.shippingAddress,
      billingAddress: historical.billingAddress,
      currency: historical.currency,
      shippingFee: historical.shippingFee,
      handlingFee: historical.handlingFee,
      omitTotal: historical.omitTotal ?? false,
      internalNotes: historical.internalNotes,
      lines: historical.lines.map((line) => ({
        ...line,
        id: undefined,
      })),
      id: current.id,
      version: current.version,
      status: current.status,
      lockVersion: current.lockVersion,
      quoteNumber: current.quoteNumber,
      prefix: current.prefix,
      customerName: current.customerName,
      organization: current.organization,
      contact: current.contact,
      quoteDate: current.quoteDate,
      validUntil: current.validUntil,
    });
    setEditing(true);
  }

  function viewVersion(version: CaseQuoteDocument) {
    if (dirty && !window.confirm("存在未保存修改，确认切换版本？")) return;
    setReuseQuoteDocumentId(null);
    setData((current) =>
      current ? { ...current, document: clone(version) } : current,
    );
    setEditing(false);
    setDirty(false);
  }

  async function save() {
    if (!data) return null;
    setSaving(true);
    setError("");
    try {
      const response = await saveCaseQuoteDocument(
        caseId,
        buildQuotePayload(data, reuseQuoteDocumentId),
      );
      setData(response);
      setReuseQuoteDocumentId(null);
      setDirty(false);
      setEditing(false);
      onChanged();
      return response;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
      return null;
    } finally {
      setSaving(false);
    }
  }

  function close() {
    if (dirty && !window.confirm("存在未保存修改，确认放弃并关闭？")) return;
    setOpen(false);
    setEditing(false);
    setDirty(false);
    void reload();
  }

  if (loading && !data)
    return (
      <section className="case-quote-panel">
        <h2>报价单</h2>
        <small>正在读取报价信息…</small>
      </section>
    );

  return (
    <section className="case-quote-panel">
      <h2>报价单</h2>
      {error && !open && <p className="case-quote-error">{error}</p>}
      {data && (
        <>
          <div className="case-quote-summary">
            <strong>
              v{data.summary.version} · {statusName[data.summary.status]}
            </strong>
            <small>{data.summary.quoteNumber}</small>
            <p>
              {data.summary.itemCount} 个产品 · 总额{" "}
              {data.summary.omitTotal
                ? ""
                : money(data.summary.total, data.summary.currency)}
            </p>
            <p>有效期至 {data.summary.validUntil}</p>
            {data.costVisible && data.summary.grossProfit !== null && (
              <p>
                毛利 {money(data.summary.grossProfit, data.summary.currency)} ·
                毛利率 {((data.summary.grossMarginBps ?? 0) / 100).toFixed(2)}%
              </p>
            )}
          </div>
          <button
            type="button"
            className="case-primary"
            disabled={disabled || (!canManage && !data.exists)}
            onClick={() => {
              setOpen(true);
              setEditing(!data.exists && data.canEdit);
            }}
          >
            {data.exists ? "管理报价单" : "新建报价单"}
          </button>
        </>
      )}
      {open && data && (
        <div className="case-quote-overlay" role="presentation">
          <div
            className="case-quote-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="case-quote-title"
          >
            <header>
              <div>
                <h2
                  id="case-quote-title"
                  className="case-document-version-title"
                >
                  <span>报价单</span>
                  <select
                    aria-label="报价单版本"
                    value={data.document.id ?? "initial"}
                    onChange={(event) => {
                      const version = data.versions.find(
                        (item) => item.id === event.target.value,
                      );
                      if (version) viewVersion(version);
                    }}
                  >
                    {!data.document.id && <option value="initial">v1</option>}
                    {data.versions.map((version) => (
                      <option key={version.id} value={version.id ?? ""}>
                        v{version.version} · {statusName[version.status]}
                      </option>
                    ))}
                  </select>
                </h2>
                <p>维护客户信息、产品售价和轻量成本毛利。</p>
              </div>
              <div className="case-quote-header-actions">
                <button type="button" onClick={() => setEditing(false)}>
                  预览
                </button>
                <button
                  type="button"
                  className={editing ? "case-primary" : ""}
                  disabled={!data.canEdit || disabled || !viewingLatest}
                  onClick={() => setEditing(true)}
                >
                  编辑
                </button>
                <button type="button" aria-label="关闭" onClick={close}>
                  ×
                </button>
              </div>
            </header>
            <main>
              {error && <p className="case-quote-error">{error}</p>}
              {data.historyCandidates.length > 0 && (
                <section className="case-quote-history">
                  <h3>完全相同订单的近期报价</h3>
                  <p>
                    仅展示同一客户、完整产品需求一致且 30 天内有效的已提交报价。
                  </p>
                  {data.historyCandidates.map((candidate) => (
                    <div key={candidate.documentId}>
                      <span>
                        {candidate.sourceCaseNumber} · {candidate.quoteNumber} ·
                        v{candidate.version} ·{" "}
                        {candidate.document.omitTotal
                          ? ""
                          : money(candidate.total, candidate.currency)}{" "}
                        ·{" "}
                        {new Date(candidate.submittedAt).toLocaleDateString(
                          "zh-CN",
                        )}
                      </span>
                      {editing && (
                        <button
                          type="button"
                          onClick={() => applyHistory(candidate)}
                        >
                          引用此报价
                        </button>
                      )}
                    </div>
                  ))}
                </section>
              )}
              <section className="case-quote-section">
                <h3>报价信息</h3>
                <div className="case-quote-form-grid">
                  <label>
                    <span>报价前缀</span>
                    <input
                      value={data.document.prefix}
                      readOnly={!editing}
                      onChange={(event) =>
                        updateDocument({ prefix: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    <span>报价编号</span>
                    <input
                      value={data.document.quoteNumber}
                      readOnly={!editing}
                      onChange={(event) =>
                        updateDocument({ quoteNumber: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    <span>客户名称</span>
                    <input
                      value={data.document.customerName}
                      readOnly={!editing}
                      onChange={(event) =>
                        updateDocument({ customerName: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    <span>组织</span>
                    <input
                      value={data.document.organization}
                      readOnly={!editing}
                      onChange={(event) =>
                        updateDocument({ organization: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    <span>联系人</span>
                    <input
                      value={data.document.contact}
                      readOnly={!editing}
                      onChange={(event) =>
                        updateDocument({ contact: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    <span>付款条件</span>
                    <input
                      value={data.document.paymentTerms}
                      readOnly={!editing}
                      onChange={(event) =>
                        updateDocument({ paymentTerms: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    <span>国家</span>
                    <input
                      value={data.document.country}
                      readOnly={!editing}
                      onChange={(event) =>
                        updateDocument({ country: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    <span>币种</span>
                    <select
                      value={data.document.currency}
                      disabled={!editing}
                      onChange={(event) => setCurrency(event.target.value)}
                    >
                      {currencies.map((currency) => (
                        <option key={currency}>{currency}</option>
                      ))}
                    </select>
                  </label>
                  <label className="case-quote-wide">
                    <span>收货地址</span>
                    <input
                      value={data.document.shippingAddress}
                      readOnly={!editing}
                      onChange={(event) =>
                        updateDocument({ shippingAddress: event.target.value })
                      }
                    />
                  </label>
                  <label className="case-quote-wide">
                    <span>账单地址</span>
                    <input
                      value={data.document.billingAddress}
                      readOnly={!editing}
                      onChange={(event) =>
                        updateDocument({ billingAddress: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    <span>报价日期</span>
                    <input
                      type="date"
                      value={data.document.quoteDate}
                      readOnly={!editing}
                      onChange={(event) =>
                        updateDocument({ quoteDate: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    <span>有效期</span>
                    <input
                      type="date"
                      value={data.document.validUntil}
                      readOnly={!editing}
                      onChange={(event) =>
                        updateDocument({ validUntil: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    <span>运费</span>
                    <input
                      type="number"
                      min="0"
                      step="0.0001"
                      value={data.document.shippingFee}
                      readOnly={!editing}
                      onChange={(event) =>
                        updateDocument({ shippingFee: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    <span>手续费</span>
                    <input
                      type="number"
                      min="0"
                      step="0.0001"
                      value={data.document.handlingFee}
                      readOnly={!editing}
                      onChange={(event) =>
                        updateDocument({ handlingFee: event.target.value })
                      }
                    />
                  </label>
                </div>
              </section>
              <section className="case-quote-section">
                <div className="case-quote-section-title">
                  <h3>报价产品</h3>
                  {editing && (
                    <button
                      type="button"
                      disabled={loadingSourcing || saving || disabled}
                      onClick={() => void chooseSourcing()}
                    >
                      {loadingSourcing
                        ? "正在读取查货方案…"
                        : "从查货单选择方案"}
                    </button>
                  )}
                  {editing && (
                    <button
                      type="button"
                      onClick={() =>
                        updateDocument({
                          lines: [
                            ...data.document.lines,
                            blankLine(data.document.currency),
                          ],
                        })
                      }
                    >
                      + 新增产品
                    </button>
                  )}
                </div>
                {notice && <p role="status">{notice}</p>}
                {sourcingError && (
                  <p className="case-quote-error" role="alert">
                    {sourcingError}
                  </p>
                )}
                {editing && sourcing && (
                  <section
                    className="case-quote-section"
                    aria-label="选择查货方案"
                  >
                    <h3>
                      查货单{" "}
                      {sourcing.sourcingVersion
                        ? `v${sourcing.sourcingVersion}`
                        : ""}{" "}
                      · 选择供应商及价格档
                    </h3>
                    <p>
                      按产品分组，可多选不同规格或供应商价格档，每个方案生成独立报价行。客户单价留空，包装数默认1，请按客户需求核对。
                    </p>
                    {!sourcing.candidates.length && (
                      <p>
                        暂无可选方案，请先保存正式查货单并将产品标记为“已找到”。
                      </p>
                    )}
                    {groupSourcingCandidates(sourcing.candidates).map(
                      ([productId, candidates]) => {
                        const product = candidates[0]!.line;
                        const addedTiers = new Set(
                          data.document.lines.map(
                            (line) => line.sourcePriceTierId,
                          ),
                        );
                        const tierIds = new Set(
                          candidates.map(
                            (candidate) => candidate.line.sourcePriceTierId,
                          ),
                        );
                        return (
                          <fieldset
                            key={productId}
                            className="case-quote-product-group"
                          >
                            <legend>
                              {product.productName} ·{" "}
                              {product.catalogNumber ||
                                product.casNo ||
                                "无编号"}
                            </legend>
                            <p>
                              客户需求：{product.requestedQuantity || "待确认"}
                              {candidates.some((candidate) =>
                                addedTiers.has(
                                  candidate.line.sourcePriceTierId,
                                ),
                              ) &&
                                " · 已添加的方案不可重复选择，可继续选择其他规格或方案。"}
                            </p>
                            <div className="case-quote-table-scroll">
                              <table className="case-quote-table case-quote-candidates">
                                <thead>
                                  <tr>
                                    <th>选择</th>
                                    <th>供应商名称</th>
                                    <th>纯度</th>
                                    <th>包装规格</th>
                                    <th>查货原价</th>
                                    <th>货期</th>
                                    <th>价格有效期</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {candidates.map((candidate) => {
                                    const tierId =
                                      candidate.line.sourcePriceTierId!;
                                    const added = addedTiers.has(tierId);
                                    return (
                                      <tr key={tierId}>
                                        <td>
                                          <input
                                            type="checkbox"
                                            aria-label={`选择 ${product.productName} ${candidate.supplierLabel} ${candidate.line.specValue} ${candidate.line.specUnit} ${candidate.purity ?? ""}`}
                                            disabled={
                                              added || candidate.expired
                                            }
                                            checked={
                                              added ||
                                              selectedTiers.includes(tierId)
                                            }
                                            onChange={() =>
                                              setSelectedTiers((current) =>
                                                current.includes(tierId)
                                                  ? current.filter(
                                                      (id) => id !== tierId,
                                                    )
                                                  : [...current, tierId],
                                              )
                                            }
                                          />
                                          {added
                                            ? "已添加"
                                            : candidate.expired
                                              ? "已过期"
                                              : ""}
                                        </td>
                                        <td>
                                          {candidate.supplierLabel}
                                          {candidate.isBestSupplier &&
                                            "（最优）"}
                                        </td>
                                        <td>{candidate.purity || "—"}</td>
                                        <td>
                                          {candidate.line.specValue}{" "}
                                          {candidate.line.specUnit}
                                        </td>
                                        <td className="case-quote-source-price">
                                          <strong>
                                            {money(
                                              candidate.line.sourceUnitPrice ??
                                                candidate.line.costUnitPrice,
                                              candidate.line.sourceCurrency ??
                                                candidate.line.costCurrency ??
                                                "",
                                            )}
                                          </strong>
                                          <small>每包装原价</small>
                                        </td>
                                        <td>
                                          {candidate.line.leadTime || "—"}
                                        </td>
                                        <td>
                                          {candidate.validUntil || "未设置"}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            {candidates.some((candidate) =>
                              selectedTiers.includes(
                                candidate.line.sourcePriceTierId!,
                              ),
                            ) && (
                              <button
                                type="button"
                                onClick={() =>
                                  setSelectedTiers((current) =>
                                    current.filter((id) => !tierIds.has(id)),
                                  )
                                }
                              >
                                清除此产品选择
                              </button>
                            )}
                          </fieldset>
                        );
                      },
                    )}
                    <button
                      type="button"
                      disabled={!selectedTiers.length}
                      className="case-primary"
                      onClick={appendSelectedSourcing}
                    >
                      追加所选报价项
                    </button>
                    <button type="button" onClick={() => setSourcing(null)}>
                      取消选择
                    </button>
                  </section>
                )}
                {editing &&
                  data.document.lines.some(
                    (line) =>
                      line.sourcePriceTierId || line.sourceSourcingProductId,
                  ) && (
                    <p>
                      查货来源的产品信息、纯度、包装规格和交期只读。更换条件请删除对应行后重新选择查货方案；包装数和客户单价可填写。
                    </p>
                  )}
                {!data.document.lines.length && (
                  <p>请从查货单选择方案，或新增产品。</p>
                )}
                <div className="case-quote-table-scroll">
                  <table className="case-quote-table">
                    <thead>
                      <tr>
                        <th>产品名称</th>
                        <th>Cat No</th>
                        <th>CAS</th>
                        <th>客户需求</th>
                        <th>纯度</th>
                        <th>规格说明</th>
                        <th>包装数</th>
                        <th>规格值</th>
                        <th>规格单位</th>
                        {(data.canEdit || data.costVisible) && (
                          <th>查货原价</th>
                        )}
                        <th>客户单价</th>
                        <th>币种</th>
                        <th>货期</th>
                        {data.costVisible && <th>内部成本</th>}
                        {data.costVisible && <th>毛利率</th>}
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortQuoteLines(
                        data.document.lines.map((line, index) => ({
                          catalogNumber: line.catalogNumber,
                          line,
                          index,
                        })),
                      ).map(({ line, index }) => {
                        const sourceLocked = Boolean(
                          line.sourcePriceTierId ||
                          line.sourceSourcingProductId,
                        );
                        const lineTotal =
                          number(line.quantity) * number(line.unitPrice);
                        const comparable =
                          line.costUnitPrice !== null &&
                          line.costCurrency === data.document.currency;
                        const lineProfit = comparable
                          ? lineTotal -
                            number(line.quantity) * number(line.costUnitPrice)
                          : null;
                        return (
                          <tr key={line.id ?? `quote-line-${index}`}>
                            <td>
                              <input
                                value={line.productName}
                                readOnly={!editing || sourceLocked}
                                onChange={(event) =>
                                  updateLine(index, {
                                    productName: event.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <input
                                value={line.catalogNumber}
                                readOnly={!editing || sourceLocked}
                                onChange={(event) =>
                                  updateLine(index, {
                                    catalogNumber: event.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <input
                                value={line.casNo}
                                readOnly={!editing || sourceLocked}
                                onChange={(event) =>
                                  updateLine(index, {
                                    casNo: event.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <input
                                value={line.requestedQuantity}
                                readOnly={!editing || sourceLocked}
                                onChange={(event) =>
                                  updateLine(index, {
                                    requestedQuantity: event.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <input
                                aria-label={`产品${index + 1}纯度`}
                                value={line.purity ?? ""}
                                readOnly={!editing || sourceLocked}
                                onChange={(event) =>
                                  updateLine(index, {
                                    purity: event.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <input
                                aria-label={`产品${index + 1}规格说明`}
                                value={line.specification}
                                readOnly={!editing || sourceLocked}
                                onChange={(event) =>
                                  updateLine(index, {
                                    specification: event.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <input
                                aria-label={`产品${index + 1}包装数`}
                                type="number"
                                min="0.0001"
                                step="0.0001"
                                value={line.quantity}
                                readOnly={!editing}
                                onChange={(event) =>
                                  updateLine(index, {
                                    quantity: event.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <input
                                value={line.specValue}
                                readOnly={!editing || sourceLocked}
                                onChange={(event) =>
                                  updateLine(index, {
                                    specValue: event.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <select
                                value={line.specUnit}
                                disabled={!editing || sourceLocked}
                                onChange={(event) =>
                                  updateLine(index, {
                                    specUnit: event.target.value,
                                  })
                                }
                              >
                                <option value="">—</option>
                                {units.map((unit) => (
                                  <option key={unit}>{unit}</option>
                                ))}
                              </select>
                            </td>
                            {(data.canEdit || data.costVisible) && (
                              <td
                                className="case-quote-source-price"
                                aria-label={`产品${index + 1}查货原价`}
                              >
                                <strong>
                                  {money(
                                    line.sourceUnitPrice ?? line.costUnitPrice,
                                    line.sourceCurrency ??
                                      line.costCurrency ??
                                      "",
                                  )}
                                </strong>
                                <small>每包装原价</small>
                                {(line.sourceCurrency ?? line.costCurrency) &&
                                  (line.sourceCurrency ?? line.costCurrency) !==
                                    data.document.currency && (
                                    <small>与报价币种不同</small>
                                  )}
                              </td>
                            )}
                            <td>
                              <input
                                type="number"
                                min="0"
                                step="0.0001"
                                value={line.unitPrice}
                                readOnly={!editing}
                                onChange={(event) =>
                                  updateLine(index, {
                                    unitPrice: event.target.value,
                                  })
                                }
                              />
                              <small>
                                行金额 {money(lineTotal, line.currency)}
                              </small>
                            </td>
                            <td>
                              <select
                                value={line.currency}
                                disabled={!editing}
                                onChange={(event) =>
                                  updateLine(index, {
                                    currency: event.target.value,
                                  })
                                }
                              >
                                {currencies.map((currency) => (
                                  <option key={currency}>{currency}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                value={line.leadTime}
                                readOnly={!editing || sourceLocked}
                                onChange={(event) =>
                                  updateLine(index, {
                                    leadTime: event.target.value,
                                  })
                                }
                              />
                            </td>
                            {data.costVisible && (
                              <td className="case-quote-cost">
                                <strong>
                                  {money(
                                    line.costUnitPrice,
                                    line.costCurrency ?? "",
                                  )}
                                </strong>
                                <small>
                                  {line.supplierName || "无成本来源"}
                                </small>
                                {line.costValidUntil && (
                                  <small>有效至 {line.costValidUntil}</small>
                                )}
                              </td>
                            )}
                            {data.costVisible && (
                              <td>
                                {lineProfit === null || lineTotal === 0
                                  ? "不可计算"
                                  : `${((lineProfit / lineTotal) * 100).toFixed(2)}%`}
                              </td>
                            )}
                            <td>
                              {editing ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const lines = [...data.document.lines];
                                    lines.splice(index, 1);
                                    updateDocument({ lines });
                                  }}
                                >
                                  删除
                                </button>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <label className="case-quote-notes">
                  <span>内部备注</span>
                  <textarea
                    value={data.document.internalNotes}
                    readOnly={!editing}
                    onChange={(event) =>
                      updateDocument({ internalNotes: event.target.value })
                    }
                  />
                </label>
                {preview && (
                  <div className="case-quote-totals">
                    <span>
                      小计{" "}
                      <strong>
                        {money(preview.subtotal, data.document.currency)}
                      </strong>
                    </span>
                    <span>
                      运费{" "}
                      <strong>
                        {money(
                          data.document.shippingFee,
                          data.document.currency,
                        )}
                      </strong>
                    </span>
                    <span>
                      手续费{" "}
                      <strong>
                        {money(
                          data.document.handlingFee,
                          data.document.currency,
                        )}
                      </strong>
                    </span>
                    <span>
                      总额{" "}
                      <strong aria-label="报价总额">
                        {data.document.omitTotal
                          ? ""
                          : money(preview.total, data.document.currency)}
                      </strong>
                      <label className="case-quote-omit-total">
                        <input
                          type="checkbox"
                          checked={data.document.omitTotal ?? false}
                          disabled={!editing || saving}
                          onChange={(event) =>
                            updateDocument({ omitTotal: event.target.checked })
                          }
                        />
                        不计算总价
                      </label>
                    </span>
                    {data.costVisible && (
                      <span>
                        总成本{" "}
                        <strong>
                          {money(preview.totalCost, data.document.currency)}
                        </strong>
                      </span>
                    )}
                    {data.costVisible && (
                      <span>
                        毛利/毛利率{" "}
                        <strong>
                          {preview.grossProfit === null
                            ? "暂不可计算"
                            : `${money(preview.grossProfit, data.document.currency)} / ${preview.grossMargin?.toFixed(2)}%`}
                        </strong>
                      </span>
                    )}
                  </div>
                )}
              </section>
            </main>
            <footer>
              <button type="button" onClick={close}>
                取消
              </button>
              <button
                type="button"
                className="case-primary"
                disabled={
                  !editing ||
                  saving ||
                  !data.canEdit ||
                  (!dirty && data.document.id !== null)
                }
                onClick={() => void save()}
              >
                {saving ? "保存中…" : "保存正式版本"}
              </button>
            </footer>
          </div>
        </div>
      )}
    </section>
  );
}
